import { jsPDF } from "jspdf";

const C = {
  graphite: [16, 20, 21], surface: [29, 34, 35], gold: [187, 161, 108],
  white: [247, 248, 246], ink: [32, 41, 43], muted: [105, 117, 119],
  line: [223, 229, 228], light: [245, 247, 246], green: [35, 129, 91],
  amber: [164, 105, 43],
};
const resultLabels = {
  conforming: "Conforme", non_conforming: "Não conforme", not_applicable: "Não aplicável",
  removed_for_maintenance: "Retirado para manutenção", damaged: "Danificado", replaced: "Substituído",
};
const actionLabels = {
  cleaning: "Limpeza", focus_adjustment: "Ajuste de foco", position_adjustment: "Ajuste de posição",
  connectorization_redone: "Conectorização refeita", communication_test: "Teste de comunicação",
  alignment: "Alinhamento", fastening: "Fixação", power_check: "Verificação de alimentação",
  cabling_check: "Verificação de cabeamento", configuration_adjustment: "Ajuste de configuração",
  firmware_update: "Atualização de firmware", component_replacement: "Troca de componente",
  lubrication: "Lubrificação", other: "Outra ação",
};
const safe = (value) => String(value ?? "").trim() || "—";
const date = (value) => value ? new Date(`${String(value).slice(0, 10)}T12:00:00`).toLocaleDateString("pt-BR") : "—";
const dateTime = (value) => value ? new Date(value).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short", timeZone: "America/Sao_Paulo" }) : "—";

const loadImage = (src, format = "JPEG") => new Promise((resolve) => {
  if (src && typeof src === "object" && src.data) { resolve(src); return; }
  const image = new Image();
  image.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const context = canvas.getContext("2d");
    if (format === "JPEG") { context.fillStyle = "#fff"; context.fillRect(0, 0, canvas.width, canvas.height); }
    context.drawImage(image, 0, 0);
    resolve({ data: canvas.toDataURL(`image/${format.toLowerCase()}`, format === "JPEG" ? .84 : undefined), format, width: canvas.width, height: canvas.height });
  };
  image.onerror = () => resolve(null);
  image.src = src;
});

// Fit an image inside its box without stretching or cropping it.
const placeImage = (pdf, image, x, y, boxWidth, boxHeight) => {
  if (!image?.width || !image?.height) return;
  const scale = Math.min(boxWidth / image.width, boxHeight / image.height);
  const width = image.width * scale, height = image.height * scale;
  pdf.addImage(image.data, image.format, x + (boxWidth - width) / 2, y + (boxHeight - height) / 2, width, height, undefined, "FAST");
};

export async function buildPreventiveReportPdf(report, { planLogo = null, secLogo = null } = {}) {
  const { activity, inspections = [], team = [] } = report;
  if (!["completed", "completed_with_issue"].includes(activity.status)) throw new Error("preventive_not_completed");

  const pdf = new jsPDF({ unit: "mm", format: "a4", compress: true });
  const left = 18, right = 192, contentWidth = right - left;
  const reportNumber = `MP-${String(activity.id).padStart(4, "0")}`;
  const txt = (value, x, y, size = 10, color = C.ink, style = "normal", options) => {
    pdf.setFont("helvetica", style); pdf.setFontSize(size); pdf.setTextColor(...color);
    pdf.text(String(value), x, y, options);
  };
  const linesFor = (value, maxWidth, size = 9) => {
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(size);
    return pdf.splitTextToSize(safe(value), maxWidth);
  };
  const writeLines = (lines, x, y, size = 9, color = C.ink, step = 4.6) => {
    lines.forEach((line, index) => txt(line, x, y + index * step, size, color));
    return y + lines.length * step;
  };
  const newPage = (section) => {
    pdf.addPage();
    pdf.setFillColor(...C.surface); pdf.rect(0, 0, 210, 31, "F");
    pdf.setFillColor(...C.gold); pdf.rect(0, 0, 2.5, 31, "F");
    if (planLogo) placeImage(pdf, planLogo, left, 6, 62, 19);
    else txt("ABOUTPLAN", left, 19, 13, C.white, "bold");
    txt(reportNumber, right, 11, 7.5, [185, 193, 189], "bold", { align: "right" });
    txt(section.toUpperCase(), right, 22, 9, C.white, "bold", { align: "right" });
    pdf.setDrawColor(...C.gold); pdf.setLineWidth(.5); pdf.line(right - 24, 27, right, 27);
    pdf.setDrawColor(...C.line); pdf.setLineWidth(.2); pdf.line(left, 38, right, 38);
    return 48;
  };
  const field = (label, value, x, y, maxWidth) => {
    txt(label.toUpperCase(), x, y, 7, C.muted, "bold");
    return writeLines(linesFor(value, maxWidth, 9.5), x, y + 6, 9.5, C.ink, 5);
  };
  const ensure = (y, required, section) => y + required > 271 ? newPage(section) : y;
  const blockText = (label, value, y, x = left, maxWidth = contentWidth, size = 8.5) => {
    const lines = linesFor(value, maxWidth, size);
    y = ensure(y, 8 + lines.length * 4.6, "Continuação da atividade");
    txt(label.toUpperCase(), x, y, 7, C.muted, "bold");
    return writeLines(lines, x, y + 6, size, C.ink) + 3;
  };

  // Capa com a mesma linguagem do relatório de referência: grafite, dourado e respiro.
  pdf.setFillColor(...C.graphite); pdf.rect(0, 0, 210, 297, "F");
  pdf.setFillColor(...C.gold); pdf.rect(0, 0, 5, 297, "F");
  pdf.setFillColor(...C.surface); pdf.circle(203, 15, 51, "F"); pdf.circle(210, 293, 62, "F");
  if (planLogo) placeImage(pdf, planLogo, 18, 24, 82, 24);
  else txt("ABOUTPLAN", 20, 43, 22, C.white, "bold");
  txt("RELATÓRIO TÉCNICO  ·  MANUTENÇÃO PREVENTIVA", 20, 106, 8.5, C.gold, "bold");
  txt("Atendimento", 20, 128, 25, C.white, "bold");
  txt("preventivo", 20, 142, 25, C.white, "bold");
  const titleLines = linesFor(activity.planName, 165, 11).slice(0, 3);
  writeLines(titleLines, 20, 158, 11, [184, 192, 189], 6);
  const coverCards = [
    ["CLIENTE / UNIDADE", `${safe(activity.clientName)}  ·  ${safe(activity.unitName)}`],
    ["ATIVIDADE", reportNumber],
    ["CONCLUSÃO", date(activity.completedAt)],
  ];
  coverCards.forEach(([label, value], index) => {
    const x = 20 + index * 59;
    pdf.setFillColor(35, 40, 40); pdf.roundedRect(x, 207, 54, 34, 3, 3, "F");
    txt(label, x + 5, 216, 6.6, C.gold, "bold");
    writeLines(linesFor(value, 44, 8.1).slice(0, 3), x + 5, 226, 8.1, C.white, 4.6);
  });
  txt(`${inspections.length} equipamento(s) inspecionado(s)`, 20, 257, 8.5, [182, 192, 187]);
  if (secLogo) placeImage(pdf, secLogo, 180, 259, 18, 23);
  txt("ABOUTPLAN  /  ABOUTSEC", 20, 278, 7, C.gold, "bold");

  let y = newPage("Resumo da execução");
  txt("Resumo do atendimento", left, y, 17, C.ink, "bold"); y += 11;
  pdf.setFillColor(...C.light); pdf.roundedRect(left, y, contentWidth, 64, 3, 3, "F");
  field("Cliente", activity.clientName, 24, y + 10, 75);
  field("Unidade", activity.unitName, 109, y + 10, 75);
  field("Plano", activity.planName, 24, y + 35, 75);
  field("Grupo / equipamento", activity.groupNumber ? `Grupo ${activity.groupNumber}` : activity.planName, 109, y + 35, 75);
  y += 73;
  field("Data programada", date(activity.plannedDate), left, y, 75);
  field("Conclusão", dateTime(activity.completedAt), 109, y, 78);
  y += 21;
  field("Responsável", activity.assignedTo || "Não informado", left, y, 75);
  field("Equipe", team.join(", ") || "Não informada", 109, y, 78);
  y += 26;
  const resultColors = {
    conforming: [91, 151, 124], non_conforming: [183, 111, 107],
    not_applicable: [151, 163, 165], removed_for_maintenance: [192, 150, 100],
    damaged: [158, 117, 136], replaced: [112, 142, 169],
  };
  const resultGroups = Object.entries(resultLabels).map(([key, label]) => ({
    label, color: resultColors[key], value: inspections.filter((item) => item.result === key).length,
  })).filter((item) => item.value > 0);
  const otherResults = inspections.length - resultGroups.reduce((sum, item) => sum + item.value, 0);
  if (otherResults > 0) resultGroups.push({ label: "Outro resultado", color: [136, 146, 148], value: otherResults });
  txt("Resultados dos equipamentos", left, y, 12, C.ink, "bold");
  txt(`${inspections.length} ${inspections.length === 1 ? "item vistoriado" : "itens vistoriados"}`, right, y, 8, C.muted, "normal", { align: "right" });
  y += 7;
  pdf.setFillColor(...C.light); pdf.roundedRect(left, y, contentWidth, 66, 3, 3, "F");
  const centerX = 53, centerY = y + 31;
  if (!inspections.length) { pdf.setFillColor(214, 222, 220); pdf.circle(centerX, centerY, 22, "F"); }
  else {
    let angle = -Math.PI / 2;
    resultGroups.forEach((group) => {
      const end = angle + (group.value / inspections.length) * Math.PI * 2;
      const path = [{ op: "m", c: [centerX, centerY] }];
      for (let cursor = angle; cursor < end; cursor += Math.PI / 90)
        path.push({ op: "l", c: [centerX + Math.cos(cursor) * 22, centerY + Math.sin(cursor) * 22] });
      path.push({ op: "l", c: [centerX + Math.cos(end) * 22, centerY + Math.sin(end) * 22] }, { op: "h" });
      pdf.setFillColor(...group.color); pdf.path(path).fill();
      angle = end;
    });
  }
  pdf.setFillColor(...C.light); pdf.circle(centerX, centerY, 13, "F");
  txt(inspections.length, centerX, centerY + 1, 17, C.ink, "bold", { align: "center" });
  txt("ITENS", centerX, centerY + 7, 6.5, C.muted, "bold", { align: "center" });
  if (!resultGroups.length) txt("Nenhum resultado registrado", 91, y + 33, 9, C.muted);
  resultGroups.forEach((group, index) => {
    const rowY = y + 13 + index * 8;
    pdf.setFillColor(...group.color); pdf.roundedRect(91, rowY - 3, 3.5, 3.5, 1, 1, "F");
    txt(group.label, 98, rowY, 8.2, C.ink);
    txt(`${group.value}  ·  ${Math.round(group.value / inspections.length * 100)}%`, 184, rowY, 8.2, C.ink, "bold", { align: "right" });
  });
  y += 74;
  y = newPage("Equipamentos inspecionados");
  txt("Resultado por equipamento", left, y, 17, C.ink, "bold"); y += 12;
  if (!inspections.length) y = blockText("Inspeções", "Nenhuma inspeção de equipamento foi registrada nesta preventiva.", y);
  for (let index = 0; index < inspections.length; index++) {
    const item = inspections[index];
    const title = `${index + 1}. ${safe(item.name)}`;
    const detail = [item.assetType, item.brand, item.model, item.area, item.location].filter(Boolean).join(" · ");
    const actionText = (item.actions || []).map((action) => actionLabels[action] || action).join(", ");
    const photoImages = (await Promise.all((item.photos || []).map(async (entry) => ({
      image: await loadImage(typeof entry === "string" ? entry : entry?.dataUrl),
      caption: typeof entry === "string" ? "" : String(entry?.caption || "").trim(),
    })))).filter((entry) => entry.image);
    const titleLines = linesFor(title, 155, 10).slice(0, 2);
    const detailLines = detail ? linesFor(detail, 169, 8) : [];
    const actionLines = actionText ? linesFor(actionText, 169, 8.5) : [];
    const noteLines = item.notes ? linesFor(item.notes, 169, 8.5) : [];
    const detailHeight = 26 + detailLines.length * 4.4 + actionLines.length * 4.6 + noteLines.length * 4.6 + 11;
    y = ensure(y, Math.min(detailHeight + (photoImages.length ? 56 : 0), 205), "Equipamentos inspecionados");
    const drawItemHeading = (atY, continued = false) => {
      const headingHeight = 13 + (titleLines.length - 1) * 4.6;
      pdf.setFillColor(...C.light); pdf.roundedRect(left, atY, contentWidth, headingHeight, 2, 2, "F");
      writeLines(titleLines, left + 4, atY + 6, 10, C.ink, 4.6);
      txt(`TAG ${safe(item.tag)}  ·  ITEM ${safe(item.itemNumber)}${continued ? "  ·  CONTINUAÇÃO" : ""}`, left + 4, atY + headingHeight - 2, 6.5, C.muted);
      return atY + headingHeight + 7;
    };
    y = drawItemHeading(y);
    txt(resultLabels[item.result] || safe(item.result), left, y, 9, item.result === "conforming" ? C.green : C.amber, "bold"); y += 7;
    if (detailLines.length) y = writeLines(detailLines, left, y, 8, C.muted, 4.4) + 2;
    if (actionLines.length) { y = ensure(y, actionLines.length * 4.6 + 6, "Equipamentos inspecionados"); txt("AÇÕES", left, y, 7, C.muted, "bold"); y = writeLines(actionLines, left, y + 5, 8.5) + 2; }
    if (noteLines.length) { y = ensure(y, noteLines.length * 4.6 + 6, "Equipamentos inspecionados"); txt("OBSERVAÇÕES", left, y, 7, C.muted, "bold"); y = writeLines(noteLines, left, y + 5, 8.5) + 2; }
    y = ensure(y, 7, "Equipamentos inspecionados");
    txt(`Inspecionado por ${safe(item.inspectedByName)} em ${dateTime(item.inspectedAt)}${item.qrVerifiedAt ? "  ·  QR validado" : ""}`, left, y, 7, C.muted); y += 9;
    for (let photoIndex = 0; photoIndex < photoImages.length; photoIndex += 2) {
      if (y + 62 > 271) { y = newPage("Evidências da atividade"); y = drawItemHeading(y, true); }
      photoImages.slice(photoIndex, photoIndex + 2).forEach((photo, offset) => {
        const x = left + offset * 89;
        pdf.setFillColor(...C.light); pdf.roundedRect(x, y, 84, 56, 2, 2, "F");
        placeImage(pdf, photo.image, x + 2, y + 2, 80, photo.caption ? 46 : 52);
        if (photo.caption) { pdf.setFontSize(6.5); txt(pdf.splitTextToSize(photo.caption, 78)[0], x + 3, y + 53, 6.5, C.muted); }
      });
      y += 62;
    }
    y = ensure(y, 6, "Equipamentos inspecionados");
    pdf.setDrawColor(...C.line); pdf.line(left, y, right, y); y += 9;
  }
  const pages = pdf.getNumberOfPages();
  for (let page = 2; page <= pages; page++) {
    pdf.setPage(page); pdf.setDrawColor(...C.line); pdf.line(left, 281, right, 281);
    txt(`${safe(activity.clientName)}  ·  ${safe(activity.unitName)}  ·  ${reportNumber}`, left, 287, 7, C.muted);
    txt(`${page} / ${pages}`, right, 287, 7, C.muted, "normal", { align: "right" });
  }
  return pdf;
}

export async function downloadPreventiveReportPdf({ token, activityId, apiBase = "/api" }) {
  const response = await fetch(`${apiBase}/maintenance/activities/${activityId}/report`, {
    headers: { Authorization: `Bearer ${token}` }, cache: "no-store",
  });
  if (!response.ok) throw new Error("preventive_report_unavailable");
  const report = await response.json();
  const [planLogo, secLogo] = await Promise.all([
    loadImage("/aboutplan-logo-white-hq.png", "PNG"),
    loadImage("/aboutsec-logo-white.png", "PNG"),
  ]);
  const pdf = await buildPreventiveReportPdf(report, { planLogo, secLogo });
  pdf.save(`Preventiva_MP-${String(report.activity.id).padStart(4, "0")}_${String(report.activity.completedAt || "").slice(0, 10)}.pdf`);
}
